import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, List, Filter, ChevronDown } from "lucide-react";
import { PlanCard } from "@/components/ui/PlanCard";
import { PlanListRow } from "@/components/ui/PlanListRow";
import DetailPlan from "components/Plans/detailPlan";
import PlanPayment from "components/Plans/payment";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Helmet } from "react-helmet-async";
import { PackageUI } from "@/api/type";
import { getPackagesList } from "@/api/package";

type ViewMode = "grid" | "list";

const statusOptions = [
  { value: "", label: "Tất cả trạng thái" },
  { value: "open", label: "Đang mở" },
  { value: "closed", label: "Đã đóng" },
  { value: "expired", label: "Hết hạn" },
];

const PlansMainPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<number>(1);
  const [selectedPlan, setSelectedPlan] = useState<PackageUI | null>(null);
  const [plansData, setPlansData] = useState<PackageUI[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const filteredPlans = useMemo(() => {
    return plansData.filter((plan) => {
      return filterStatus === "" || plan.status === filterStatus;
    });
  }, [filterStatus, plansData]);

  const handleViewDetail = (plan: PackageUI) => {
    if (plan.status !== "open") return;
    navigate(`/plans/${plan.id}`);
  };

  const getPageTitle = () => {
    if (location.pathname.includes('plan')) return 'Gói đầu tư';
    return 'Gói sản phẩm';
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Dashboard', path: '/home' },
    { label: getPageTitle() },
  ];

  useEffect(() => {
    setLoading(true);
    const fetchPlans = async () => {
      // Simulate API call
      getPackagesList().then((data) => {
        const mappedPlans: PackageUI[] = data.data.map((pkg) => ({
          id: pkg.id,
          name: pkg.name,
          description: pkg.description,
          type: "fixed", // Placeholder, map accordingly
          status: pkg.is_active ? "open" : "closed",
          image: pkg.image, // Placeholder, map accordingly
          minInvestment: pkg.price ? parseFloat(pkg.price) : 0,
          maxInvestment: pkg.price ? parseFloat(pkg.price) * 10 : 0, // Example mapping
          profitRate: parseFloat(pkg.min_profit_rate), // Example mapping
          profitPeriod: "month", // Placeholder, map accordingly
          duration: pkg.duration_months,
          commissionF1: parseFloat(pkg.commission_rate), // Example mapping
          paymentCycle: "Hàng tháng", // Placeholder, map accordingly
          weight: pkg.part_per_user,
          total_weight: 210,
          maxSlot: pkg.total_slots,
          usedSlot: pkg.used_slots || 0,
        }));
        setPlansData(mappedPlans);
      });
    };
    fetchPlans();
    setLoading(false);
  }, []);

  return (
    <div className="space-y-6 overflow-x-hidden">
      <Helmet>
        <title>{getPageTitle()} - BeQs Hodings Reward</title>
      </Helmet>
      <PageHeader title={getPageTitle()} breadcrumbs={breadcrumbs} />
      {step === 1 && (
        loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 overflow-x-hidden">
              <div>
                <h2 className="!text-text-secondary dark:!text-dark-text-secondary mt-1">
                  Chọn gói đầu tư phù hợp với bạn
                </h2>
              </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* View Toggle */}
              <div className="flex !border !border-gray-200 dark:!border-dark-border !rounded-md overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`!p-2 !border-none !transition-colors ${viewMode === "grid"
                      ? "!bg-primary !text-white"
                      : "!bg-white dark:!bg-dark-card !text-gray-500 dark:!text-dark-text-secondary hover:!bg-gray-50 dark:hover:!bg-dark-card-elevated"
                    }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`!p-2 !border-none !border-l !border-gray-200 dark:!border-dark-border !transition-colors ${viewMode === "list"
                      ? "!bg-primary !text-white"
                      : "!bg-white dark:!bg-dark-card !text-gray-500 dark:!text-dark-text-secondary hover:!bg-gray-50 dark:hover:!bg-dark-card-elevated"
                      }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>

                {/* Filter Icon */}
                <button className="!p-2 !bg-white dark:!bg-dark-card !border !border-gray-200 dark:!border-dark-border !rounded-md !text-gray-500 dark:!text-dark-text-secondary hover:!bg-gray-50 dark:hover:!bg-dark-card-elevated !transition-colors">
                  <Filter className="w-4 h-4" />
                </button>

                {/* Status Filter */}
                <div className="relative">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="!px-4 !py-2 !pr-10 !bg-white dark:!bg-dark-card !border !border-gray-200 dark:!border-dark-border !rounded-md !text-sm !text-gray-700 dark:!text-dark-text-secondary !appearance-none !cursor-pointer focus:!outline-none focus:!border-primary"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 !text-gray-400 dark:!text-dark-text-muted pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Grid View */}
            {viewMode === "grid" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredPlans.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} onViewDetail={handleViewDetail} />
                ))}
              </div>
            )}

          {/* List View */}
          {viewMode === "list" && (
            <div className="w-full overflow-hidden">
              <div className="!bg-white dark:!bg-dark-card !rounded-md !shadow-sm overflow-hidden">
                <div className="w-full overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr className="!bg-gray-50 dark:!bg-dark-card-elevated !border-b !border-gray-100 dark:!border-dark-border">
                        <th className="px-4 py-3 text-left !text-xs !font-medium !text-gray-900 dark:!text-dark-text uppercase tracking-wider whitespace-nowrap">
                          Tên gói
                        </th>
                        <th className="px-4 py-3 text-left !text-xs !font-medium !text-gray-900 dark:!text-dark-text uppercase tracking-wider whitespace-nowrap">
                          Trạng thái
                        </th>
                        <th className="px-4 py-3 text-left !text-xs !font-medium !text-gray-900 dark:!text-dark-text uppercase tracking-wider whitespace-nowrap">
                          Tổng đầu tư
                        </th>
                        <th className="px-4 py-3 text-center !text-xs !font-medium !text-gray-900 dark:!text-dark-text uppercase tracking-wider whitespace-nowrap">
                          <div>Lợi nhuận</div>
                          <div className="!text-[10px] !text-gray-400 dark:!text-dark-text !normal-case">Tối thiểu</div>
                        </th>
                        <th className="px-4 py-3 text-center !text-xs !font-medium !text-gray-900 dark:!text-dark-text uppercase tracking-wider whitespace-nowrap">
                          Thời hạn
                        </th>
                        <th className="px-4 py-3 text-center !text-xs !font-medium !text-gray-900 dark:!text-dark-text uppercase tracking-wider whitespace-nowrap">
                          <div>Hoa hồng</div>
                          <div className="!text-[10px] !text-gray-400 dark:!text-dark-text !normal-case">F1</div>
                        </th>
                        <th className="px-4 py-3 text-center !text-xs !font-medium !text-gray-900 dark:!text-dark-text uppercase tracking-wider whitespace-nowrap">
                          Slot
                        </th>
                        <th className="px-4 py-3 text-center !text-xs !font-medium !text-gray-900 dark:!text-dark-text uppercase tracking-wider whitespace-nowrap">
                          Hành động
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPlans.map((plan) => (
                        <PlanListRow key={plan.id} plan={plan} onViewDetail={handleViewDetail} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

            {filteredPlans.length === 0 && (
              <div className="!bg-white dark:!bg-dark-card !rounded-xl !p-12 text-center">
                <div className="flex items-center justify-center">
                  <img src="/loading.svg" alt="Loading" />
                </div>
                <p className="!text-gray-500 dark:!text-dark-text">Đang tải dữ liệu...</p>
              </div>
            )}
          </>
        )
      )}

      {step === 2 && selectedPlan && (
        <div className="plan-detail-container">
          <DetailPlan
            id={selectedPlan.id}
            back={() => {
              setStep(1);
              const url = new URL(window.location.href);
              url.searchParams.delete("id");
              window.history.replaceState({}, "", url.toString());
            }}
            next={() => setStep(3)}
          />
        </div>
      )}

      {step === 3 && (
        <div className="plan-enroll-container">
          <PlanPayment onComplete={() => setStep(1)} onBack={() => setStep(2)} />
        </div>
      )}
    </div>
  );
};

export default PlansMainPage;